import { format, parseISO } from 'date-fns';

import { useResolvedTheme } from '../theme';
import Modal, { Button } from './Modal';
import { usePlanActions } from './data';
import type { Plan, PlanEvent, Rhythm } from './types';
import { cadenceLabel, minutesLabel, timeRange, tint } from './util';

export function EventModal({
  event,
  rhythm,
  onClose,
  onOpenRhythm
}: {
  event: PlanEvent;
  rhythm?: Rhythm;
  onClose: () => void;
  onOpenRhythm: (id: string) => void;
}) {
  const theme = useResolvedTheme();
  const s = new Date(event.start);
  const e = new Date(event.end);
  return (
    <Modal
      title={event.summary ?? '(no title)'}
      accent={tint(event.color, theme).solid}
      subtitle={
        event.all_day
          ? format(parseISO(event.start.slice(0, 10)), 'EEEE, MMMM d')
          : `${format(s, 'EEEE, MMMM d')}, ${timeRange(s, e)}`
      }
      onClose={onClose}
      width={440}
    >
      {rhythm ? (
        <button
          type="button"
          onClick={() => onOpenRhythm(rhythm.id)}
          className="flex w-full items-center gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-[10px] text-left hover:border-[var(--border-strong)]"
        >
          <span className="h-[10px] w-[10px] rounded-[3px]" style={{ background: tint(rhythm.color, theme).solid }} />
          <span className="flex-1 text-[0.88rem]">Counts toward {rhythm.name}</span>
          <span className="text-[0.78rem] text-[var(--text-muted)]">{cadenceLabel(rhythm)}</span>
        </button>
      ) : (
        <p className="m-0 text-[0.86rem] text-[var(--text-muted)]">Busy time</p>
      )}
    </Modal>
  );
}

export function UnplacedModal({
  day,
  plan,
  rhythms,
  onClose,
  onOpenRhythm
}: {
  day: string;
  plan: Plan;
  rhythms: Map<string, Rhythm>;
  onClose: () => void;
  onOpenRhythm: (id: string) => void;
}) {
  const theme = useResolvedTheme();
  const actions = usePlanActions();
  const pd = plan.days.find((d) => d.date === day);
  const items = pd?.unplaced ?? [];
  return (
    <Modal title="No room" subtitle={format(parseISO(day), 'EEEE, MMMM d')} onClose={onClose} width={460}>
      <div className="flex flex-col">
        {items.map((u) => {
          const r = rhythms.get(u.rhythm_id);
          if (!r) return null;
          return (
            <div key={u.id} className="flex items-center gap-3 rounded-md px-2 py-[7px] hover:bg-[var(--bg-hover)]">
              <span className="h-[10px] w-[10px] rounded-[3px]" style={{ background: tint(r.color, theme).solid }} />
              <button type="button" onClick={() => onOpenRhythm(r.id)} className="min-w-0 flex-1 truncate text-left text-[0.88rem]">
                {r.name}
              </button>
              <span className="text-[0.78rem] text-[var(--text-muted)]">{minutesLabel(r.duration_minutes)}</span>
              <Button
                variant="ghost"
                onClick={() => actions.skip.mutate({ rhythm_id: r.id, day, skipped: true })}
              >
                Skip
              </Button>
            </div>
          );
        })}
        {items.length === 0 && <p className="m-0 text-[0.86rem] text-[var(--text-muted)]">Everything fits.</p>}
      </div>
      {pd && pd.skipped.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-1 text-[0.72rem] font-medium uppercase tracking-wide text-[var(--text-faint)]">Skipped</h3>
          {pd.skipped.map((id) => {
            const r = rhythms.get(id);
            if (!r) return null;
            return (
              <div key={id} className="flex items-center gap-3 rounded-md px-2 py-[7px] hover:bg-[var(--bg-hover)]">
                <span className="h-[10px] w-[10px] rounded-[3px] opacity-50" style={{ background: tint(r.color, theme).solid }} />
                <span className="min-w-0 flex-1 truncate text-[0.88rem] text-[var(--text-muted)]">{r.name}</span>
                <Button variant="ghost" onClick={() => actions.skip.mutate({ rhythm_id: id, day, skipped: false })}>
                  Unskip
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

export function SlotModal({
  start,
  rhythms,
  onClose
}: {
  start: Date;
  rhythms: Rhythm[];
  onClose: () => void;
}) {
  const theme = useResolvedTheme();
  const actions = usePlanActions();
  return (
    <Modal title="Plan a block" subtitle={format(start, "EEEE, MMMM d 'at' h:mmaaa")} onClose={onClose} width={460}>
      <div className="grid grid-cols-2 gap-[6px]">
        {rhythms
          .filter((r) => r.active)
          .map((r) => {
            const t = tint(r.color, theme);
            const end = new Date(start.getTime() + r.duration_minutes * 60_000);
            return (
              <button
                key={r.id}
                type="button"
                disabled={actions.pin.isPending}
                onClick={() =>
                  actions.pin.mutate(
                    { rhythm_id: r.id, start: start.toISOString(), end: end.toISOString() },
                    { onSuccess: onClose }
                  )
                }
                className="flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-[8px] text-left transition-colors hover:border-[var(--border-strong)]"
                style={{ background: undefined }}
              >
                <span className="h-[10px] w-[10px] flex-shrink-0 rounded-[3px]" style={{ background: t.solid }} />
                <span className="min-w-0 flex-1 truncate text-[0.85rem]">{r.name}</span>
                <span className="text-[0.72rem] text-[var(--text-faint)]">{minutesLabel(r.duration_minutes)}</span>
              </button>
            );
          })}
      </div>
    </Modal>
  );
}
